import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { DefaultFilter } from '../../../components/common/table/default-filter';
import Table from '../../../components/common/table/table';
import { Option } from '../../../components/form/elements/select';
import FieldTypes from '../../../enums/field-types';
import { CropWorkType } from '../../../enums/crop-work-types';
import { useQuery } from '../../../hooks/use-query';
import { YieldAccounting } from '../../../interfaces/yieldAccounting';
import { CROP_YIELD_ACCOUNTING_CARGO_INDEX } from '../../../router/routes';
import { useService } from '../../../services/services';
import { route } from '../../../services/utils';
import css from './section.module.css';

export default function Section({ workTypeId }: { workTypeId: CropWorkType }) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.yield_accounting.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const [params] = useQuery();
  const { workService } = useService();
  const [nameFilter, setNameFilter] = useState<Option[]>([]);
  const [productTypes, setProductTypes] = useState([]);

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      workService.yieldAccounting(farmId, season, {
        page,
        sort,
        desc,
        typeId: workTypeId,
        ...filter,
      }),
    [workService, farmId, season, workTypeId]
  );

  useEffect(() => {
    const subscription = workService
      .usedFields(farmId, season, {
        typeId: workTypeId,
        sort: 'name',
        done: true,
      })
      .subscribe((fields) =>
        setNameFilter(
          fields.map((field) => ({ label: field.name, value: field.id }))
        )
      );

    const productTypes = workService
      .usedYieldAccountingProductTypes(farmId, season, workTypeId)
      .subscribe((types) => {
        setProductTypes(
          types.map((type) => ({ label: type.value, value: type.value }))
        );
      });
    return () => {
      subscription.unsubscribe();
      productTypes.unsubscribe();
    };
  }, [farmId, season, workTypeId, params, workService]);

  return (
    <div className={css['body']}>
      <Table<YieldAccounting>
        request={request}
        keyAccessor="yield_id"
        columns={[
          {
            accessor: 'work_date',
            header: t('pages.crop.works.date'),
            className: css['strong'],
            sortable: true,
          },
          {
            accessor: 'lad_number',
            header: t('pages.crop.fields_and_works.fields.lad_number'),
            className: css['strong'],
            sortable: true,
          },
          {
            accessor: 'name',
            header: tp('field_name'),
            className: css['strong'],
            sortable: true,
            filter: <DefaultFilter name="field_id" options={nameFilter} />,
          },
          {
            accessor: 'area_ha',
            header: tp('area_ha'),
          },
          {
            accessor: 'work_crop_value',
            header: t('pages.crop.fields_and_works.fields.crop'),
          },
          {
            accessor: 'cargo_product_types',
            header: tp('cargo.product_type'),
            dataType: FieldTypes.JSON_AGG,
            sortable: true,
            filter: (
              <DefaultFilter name="product_types" options={productTypes} />
            ),
          },
          {
            accessor: 'quantity',
            header: tp('cargo.quantity'),
            dataType: FieldTypes.JSON_AGG,
          },
          {
            accessor: 'avg_ha',
            header: tp('cargo.avg'),
            dataType: FieldTypes.JSON_AGG,
          },
        ]}
        viewRoute={route(CROP_YIELD_ACCOUNTING_CARGO_INDEX, {
          farmId: farmId,
          season: season,
        })}
        viewRecordAbility={{ title: 'viewAny cargo' }}
      />
    </div>
  );
}
